<template>
  <div id="landing-plans-table">
    <Loader :active="loading"></Loader>

    <div class="search-container">
      <b-field class="search-field">
        <b-input
          v-model.trim="search"
          placeholder="Buscar por nombre, tipo o código de cartilla"
          icon-pack="fas"
          icon="search"
          expanded
          @keyup.native.enter="searchPlans"
        ></b-input>
      </b-field>

      <b-field class="type-filter-field">
        <b-select
          v-model="typeFilter"
          placeholder="Todos los tipos"
          expanded
          @input="searchPlans"
        >
          <option value="">Todos los tipos</option>
          <option
            v-for="type in planTypes"
            :key="type.value"
            :value="type.value"
          >
            {{ type.label }}
          </option>
        </b-select>
      </b-field>

      <b-button
        type="is-primary"
        icon-pack="fas"
        icon-left="search"
        :loading="loading"
        :disabled="loading"
        @click="searchPlans"
      >
        Buscar
      </b-button>

      <b-button
        v-if="search || typeFilter"
        type="is-light"
        icon-pack="fas"
        icon-left="times"
        :disabled="loading"
        @click="clearSearch"
      >
        Limpiar
      </b-button>
    </div>

    <div class="table-container">
      <table class="table is-fullwidth is-striped is-hoverable">
        <thead>
          <tr>
            <th>Imagen</th>
            <th>Nombre</th>
            <th>Tipo</th>
            <th class="has-text-centered">Beneficios</th>
            <th>Cartilla</th>
            <th class="has-text-centered">Sitio web</th>
            <th class="has-text-centered">Ventas</th>
            <th class="has-text-centered">Acciones</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="plan in plans" :key="plan.id">
            <td>
              <div class="plan-thumbnail">
                <img
                  v-if="hasValidImage(plan)"
                  :src="plan.image"
                  :alt="plan.name"
                  @error="handleImageError(plan.id)"
                />

                <span v-else class="plan-thumbnail-empty"> Sin imagen </span>
              </div>
            </td>

            <td>
              <strong>{{ plan.name }}</strong>

              <p v-if="plan.landing_title" class="plan-subtitle">
                {{ plan.landing_title }}
              </p>

              <p v-if="plan.slug" class="plan-slug">
                {{ plan.slug }}
              </p>
            </td>

            <td>
              {{ getTypeText(plan.type) }}
            </td>

            <td class="has-text-centered">
              <span class="count-badge">
                {{ getBenefitsCount(plan) }}
              </span>
            </td>

            <td>
              <div class="cartilla-information">
                <span
                  class="visibility-badge"
                  :class="
                    isEnabled(plan.show_in_cartilla)
                      ? 'is-visible'
                      : 'is-hidden'
                  "
                >
                  {{ isEnabled(plan.show_in_cartilla) ? "Visible" : "Oculto" }}
                </span>

                <span v-if="plan.cartilla_plan_id" class="cartilla-code">
                  Código: {{ plan.cartilla_plan_id }}
                </span>

                <span v-else class="cartilla-code is-empty"> Sin código </span>
              </div>
            </td>

            <td class="has-text-centered">
              <span
                class="visibility-badge"
                :class="
                  isEnabled(plan.show_on_website) ? 'is-visible' : 'is-hidden'
                "
              >
                {{ isEnabled(plan.show_on_website) ? "Visible" : "Oculto" }}
              </span>
            </td>

            <td class="has-text-centered">
              <span
                class="visibility-badge"
                :class="
                  isEnabled(plan.show_in_sales) ? 'is-visible' : 'is-hidden'
                "
              >
                {{ isEnabled(plan.show_in_sales) ? "Visible" : "Oculto" }}
              </span>
            </td>

            <td class="has-text-centered">
              <div class="plan-actions">
                <button
                  type="button"
                  class="action-icon"
                  data-tooltip="EDITAR"
                  aria-label="Editar plan"
                  @click="editPlan(plan)"
                >
                  <i class="fas fa-pen" aria-hidden="true"></i>
                </button>

                <button
                  type="button"
                  class="action-icon"
                  data-tooltip="ELIMINAR"
                  aria-label="Eliminar plan"
                  @click="confirmDelete(plan)"
                >
                  <i class="fas fa-trash" aria-hidden="true"></i>
                </button>

                <a
                  v-if="isLandingAvailable(plan)"
                  class="action-icon"
                  :href="landingUrl(plan)"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-tooltip="VER LANDING"
                  :aria-label="`Ver landing de ${plan.name}`"
                >
                  <img src="/img/actions/link.svg" alt="" aria-hidden="true" />
                </a>
              </div>
            </td>
          </tr>

          <tr v-if="!loading && plans.length === 0">
            <td colspan="8" class="has-text-centered empty-results">
              {{
                search || typeFilter
                  ? "No se encontraron planes para la búsqueda."
                  : "No se encontraron planes."
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="total > perPage" class="pagination-container">
      <b-pagination
        :total="total"
        :current="page"
        :per-page="perPage"
        order="is-centered"
        @change="changePage"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import Loader from "@/components/Loader.vue";
import { alertFailed, alertSuccess } from "@/helpers";
import {
  LANDING_PLAN_TYPES,
  getLandingPlanTypeLabel,
} from "@/constants/landingPlanTypes";

export default {
  name: "LandingPlansTable",

  components: {
    Loader,
  },

  data() {
    return {
      loading: false,
      plans: [],
      page: 1,
      perPage: 8,
      total: 0,
      search: "",
      typeFilter: "",
      planTypes: LANDING_PLAN_TYPES,
      brokenImages: {},
    };
  },

  methods: {
    async fetchPlans() {
      try {
        this.loading = true;
        this.brokenImages = {};

        const response = await this.$repositories.landingPlans.index(
          this.page,
          this.search,
          this.perPage,
          this.typeFilter
        );

        const pagination = response.data || {};

        this.plans = Array.isArray(pagination.data) ? pagination.data : [];

        this.page = pagination.current_page || 1;
        this.perPage = pagination.per_page || 8;
        this.total = pagination.total || 0;
      } catch (error) {
        console.error("Error cargando los planes:", error);

        this.plans = [];
        this.total = 0;

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "No se pudieron cargar los planes.";

        alertFailed(message);
      } finally {
        this.loading = false;
      }
    },

    searchPlans() {
      this.page = 1;
      this.fetchPlans();
    },

    clearSearch() {
      this.search = "";
      this.typeFilter = "";
      this.page = 1;
      this.fetchPlans();
    },

    changePage(page) {
      this.page = page;
      this.fetchPlans();
    },

    editPlan(plan) {
      this.$router.push(`/landing-planes/${plan.id}/editar`);
    },

    landingUrl(plan) {
      const websiteUrl =
        process.env.VUE_APP_WEBSITE_URL || "https://osfatun.com.ar";
      return `${websiteUrl.replace(/\/$/, "")}/plans/${encodeURIComponent(
        plan.slug
      )}`;
    },

    isLandingAvailable(plan) {
      return Boolean(
        plan &&
          plan.slug &&
          this.hasRichTextContent(plan.landing_title) &&
          this.hasRichTextContent(plan.welcome_content)
      );
    },

    hasRichTextContent(value) {
      if (typeof value !== "string") {
        return false;
      }

      return Boolean(
        value
          .replace(/<[^>]*>/g, "")
          .replace(/&nbsp;/gi, " ")
          .trim()
      );
    },

    confirmDelete(plan) {
      this.$buefy.dialog.confirm({
        title: "Eliminar plan",
        message:
          "¿Seguro que deseas eliminar " + `<strong>${plan.name}</strong>?`,
        confirmText: "Eliminar",
        cancelText: "Cancelar",
        type: "is-danger",
        hasIcon: true,
        onConfirm: () => this.deletePlan(plan.id),
      });
    },

    async deletePlan(id) {
      try {
        this.loading = true;

        const response = await this.$repositories.landingPlans.delete(id);

        alertSuccess(response.data.message || "Plan eliminado correctamente.");

        if (this.plans.length === 1 && this.page > 1) {
          this.page -= 1;
        }

        await this.fetchPlans();
      } catch (error) {
        console.error("Error eliminando el plan:", error);

        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : "No se pudo eliminar el plan.";

        alertFailed(message);
      } finally {
        this.loading = false;
      }
    },

    isEnabled(value) {
      return value === true || value === 1 || value === "1" || value === "true";
    },

    getBenefitsCount(plan) {
      if (Array.isArray(plan.benefits)) {
        return plan.benefits.length;
      }

      return 0;
    },

    hasValidImage(plan) {
      return Boolean(plan.image && !this.brokenImages[plan.id]);
    },

    handleImageError(planId) {
      this.$set(this.brokenImages, planId, true);
    },

    getTypeText(type) {
      return getLandingPlanTypeLabel(type);
    },
  },

  created() {
    this.fetchPlans();
  },
};
</script>

<style lang="scss" scoped>
#landing-plans-table {
  position: relative;

  .search-container {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    max-width: 1000px;
    margin-bottom: 1.25rem;
  }

  .search-field {
    flex: 1;
    margin-bottom: 0;
  }

  .type-filter-field {
    width: 210px;
    margin-bottom: 0;
  }

  .table-container {
    width: 100%;
    max-height: 65vh;
    overflow: auto;
    border: 1px solid #eeeeee;
    border-radius: 4px;
  }

  table {
    min-width: 980px;
    margin-bottom: 0;
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #ffffff;
  }
  table {
    min-width: 980px;
  }

  th {
    vertical-align: middle;
    white-space: nowrap;
  }

  td {
    vertical-align: middle;
  }

  .plan-thumbnail {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 90px;
    height: 60px;
    overflow: hidden;
    background: #f5f5f5;
    border: 1px solid #dddddd;
    border-radius: 4px;

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .plan-thumbnail-empty {
    padding: 0.25rem;
    color: #999999;
    font-size: 0.7rem;
    text-align: center;
  }

  .plan-subtitle {
    max-width: 280px;
    margin-top: 0.2rem;
    overflow: hidden;
    color: #666666;
    font-size: 0.75rem;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .plan-slug {
    margin-top: 0.1rem;
    color: #999999;
    font-size: 0.7rem;
  }

  .count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    height: 30px;
    padding: 0 0.5rem;
    color: #ffffff;
    font-size: 0.75rem;
    font-weight: 700;
    background: #0093bf;
    border-radius: 20px;
  }

  .cartilla-information {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
  }

  .cartilla-code {
    color: #666666;
    font-size: 0.72rem;

    &.is-empty {
      color: #999999;
    }
  }

  .visibility-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 54px;
    padding: 0.2rem 0.55rem;
    font-size: 0.72rem;
    font-weight: 600;
    border-radius: 20px;

    &.is-visible {
      color: #257942;
      background: #e8f7ed;
    }

    &.is-hidden {
      color: #8a3434;
      background: #fbeaea;
    }
  }

  .plan-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
  }

  tbody tr:hover,
  tbody tr:focus-within {
    position: relative;
    z-index: 998;
  }

  .action-icon {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    color: #666666;
    background: #eeeeee;
    border: 0;
    border-radius: 50%;
    box-shadow: none;
    cursor: pointer;
    transition: color 0.2s ease, background-color 0.2s ease;

    i {
      font-size: 0.82rem;
    }

    img {
      width: 15px;
      height: 15px;
      opacity: 0.72;
    }

    &::after {
      position: absolute;
      top: calc(100% + 8px);
      right: 50%;
      z-index: 999;
      padding: 0.35rem 0.55rem;
      color: #ffffff;
      font-size: 0.68rem;
      font-weight: 600;
      line-height: 1;
      white-space: nowrap;
      content: attr(data-tooltip);
      background: #444444;
      border-radius: 4px;
      opacity: 0;
      pointer-events: none;
      transform: translateX(50%);
      transition: opacity 0.15s ease;
    }

    &:hover,
    &:focus {
      color: #333333;
      background: #d9d9d9;
      outline: none;

      img {
        opacity: 0.9;
      }

      &::after {
        opacity: 1;
        transform: translateX(50%);
      }
    }
  }

  .empty-results {
    padding: 2rem 1rem;
    color: #7a7a7a;
  }

  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 1.25rem;
  }
}

@media screen and (max-width: 768px) {
  #landing-plans-table {
    .search-container {
      flex-direction: column;

      .field,
      .button {
        width: 100%;
      }
    }
  }
}
</style>
